import { BoltOutlined, CheckCircleRounded } from '@mui/icons-material'
import { Box, Tooltip } from '@mui/material'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import CustomAccordion from '@/components/layouts/CustomAccordion'
import MarketTestList from '@/features/prequalification/localControl/components/MarketTestList'
import type { ResourceSetTestActions } from '@/features/prequalification/localControl/components/resourceSetTestActions'
import type {
  LocalPqRun,
  PqMarketCatalogueEntry,
  ResourceSetMember,
} from '@/features/prequalification/localControl/types'
import { aggregateMarket } from '@/features/prequalification/localControl/utils/aggregation'
import { glowSweepSx, pulseSx, settleInSx } from '@/features/prequalification/localControl/utils/motion'
import { getPhaseChipProps, phaseChipTooltip } from '@/features/prequalification/localControl/utils/phasePresentation'

type MarketAccordionProps = {
  market: PqMarketCatalogueEntry
  resources: ResourceSetMember[]
  runs: LocalPqRun[]
  actions: ResourceSetTestActions
}

function MarketAccordion({ market, resources, runs, actions }: Readonly<MarketAccordionProps>) {
  const { t } = useTranslation()

  const aggregate = aggregateMarket(runs, market, resources.length)
  const isRunning = aggregate.phase === 'STARTED' || aggregate.phase === 'ENQUEUED'
  const isFullyQualified =
    aggregate.hasRuns && aggregate.totalTests > 0 && aggregate.passedTests === aggregate.totalTests

  // Only sweep the success light when the market *crosses* into fully qualified
  // under the user's eyes, never when re-opening an already-qualified market.
  const wasFullyQualified = useRef(isFullyQualified)
  const justQualified = !wasFullyQualified.current && isFullyQualified
  useEffect(() => {
    wasFullyQualified.current = isFullyQualified
  })

  const chip = getPhaseChipProps(aggregate.phase, t)
  const statusProps = aggregate.hasRuns
    ? {
        label: chip.label,
        color: chip.color,
        icon: chip.icon,
        tooltipLabel: phaseChipTooltip(aggregate.phase, aggregate.scheduledStartAt, t) ?? chip.label,
      }
    : undefined

  const description = aggregate.hasRuns
    ? t('prequalifications.resource_set.market.tests_passed', {
        passed: aggregate.passedTests,
        total: aggregate.totalTests,
      })
    : t('prequalifications.resource_set.market.not_started')

  function renderToggleNode() {
    if (isRunning) {
      return (
        <Tooltip title={t('prequalifications.resource_set.market.running')}>
          <BoltOutlined color="warning" fontSize="small" sx={[{ mr: 1 }, pulseSx]} />
        </Tooltip>
      )
    }
    if (isFullyQualified) {
      return (
        <Tooltip title={t('prequalifications.resource_set.market.fully_qualified')}>
          <CheckCircleRounded color="success" fontSize="small" sx={[{ mr: 1 }, justQualified && settleInSx]} />
        </Tooltip>
      )
    }
    return undefined
  }

  return (
    <CustomAccordion
      defaultExpanded={aggregate.hasRuns}
      description={description}
      statusProps={statusProps}
      title={market.label}
      toggleNode={renderToggleNode()}
    >
      <Box sx={[{ width: '100%', borderRadius: 1 }, justQualified && glowSweepSx]}>
        <MarketTestList actions={actions} market={market} resources={resources} runs={runs} />
      </Box>
    </CustomAccordion>
  )
}

export default MarketAccordion
